import React, { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { showSnackbar } from "../features/snakbar/snackbarSlice";
import DEFAULT_ROUTES from "../constants/routes";

const ACCESS_DENIED_MESSAGE = "You do not have access to this page.";

/**
 * Route guard for role-based access. Checks allowORoles first, then blockedRoles.
 *
 * @param {React.ReactNode} children - The route content to render when access is allowed
 * @param {string[]} allowRoles - User must have at least one of these roles (checked first)
 * @param {string[]} blockedRoles - User is blocked if they have any of these roles (checked after allowORoles)
 */
const RestrictedRoute = ({
  children,
  allowRoles = [],
  blockedRoles = [],
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const userRoles = user?.roles || [];

  const failsAllowORoles =
    allowRoles.length > 0 &&
    user &&
    !allowRoles.some((role) => userRoles.includes(role));

  const hasBlockedRole =
    user && blockedRoles.some((role) => userRoles.includes(role));

  const isAccessDenied = failsAllowORoles || hasBlockedRole;

  useEffect(() => {
    if (isAccessDenied) {
      dispatch(
        showSnackbar({
          message: ACCESS_DENIED_MESSAGE,
          severity: "warning",
          duration: 4000,
        })
      );
    }
  }, [isAccessDenied, dispatch]);

  if (!user) {
    return <Navigate to={DEFAULT_ROUTES.LOGIN} />;
  }

  if (isAccessDenied) {
    return (
      <Navigate
        to={`${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}`}
        replace
      />
    );
  }

  return children;
};

export default RestrictedRoute;
